/**
 * Update the directive store using information from the environment.
 * @module
 */
var _ = require('optimizely-client-fn');

var actionTypes = require('../constants/action_types');
var ApiEvents = require('../api_events');
var Cookie = require('../utils/cookie');
var createError = require('../utils/custom_error').create;
var enums = require('../constants/enums');
var Flux = require('../flux');
var Logger = require('../utils/logger');
var JSONParseError = exports.JSONParseError = createError('JSONParseError');
var BrowserSource = require('../utils/browser');
var JSONEnv = require('../lib/json');
var WindowEnv = require('../env/window');

/**
 * Special query params used by directives.
 *
 * Parameter usage:
 *
 * * `optimizely_show_preview` - (AB_PREVIEW/LEGACY_PREVIEW) Indicates running in Classic preview mode.
 *    Disables this client if present.
 * * `optimizely_disable` (DISABLE) Disables client if present.
 * * `optimizely_editor` (EDITOR) Disables non-editor client if present.
 * * `optimizely_p13n` (EDITOR_V2) Indicates running in v2 (Optimizely X) editor.
 * * `optimizely_x_audiences` (FORCE_AUDIENCES) Comma-separated list of Audience IDs to "impersonate".
 *    If set overrides any Audiences the visitor is in "naturally".
 * * `optimizely_x` (FORCE_VARIATIONS) Comma-separated list of Variation IDs to bucket.
 *    If set overrides any variations the visitor is in "naturally".
 * * `optimizely_opt_out` (OPT_OUT) Opts visitor out of Optimizely tracking (sets cookie to persist).
 * * `optimizely_preview_layer_ids` (PREVIEW_LAYER_IDS) Comma-separated list of Campaign IDs to preview (data for given Campaigns is loaded by client).
 *    Requires `TOKEN` parameter or cookie to work.
 * * `optimizely_token` (TOKEN) Auth token for current project to load preview (unpublished/archived/paused) Campaign data.
 *    Can also be read from "optimizelyToken" cookie.
 * * `optimizely_force_tracking` (FORCE_TRACKING) Sends tracking events even if client is in preview or force_variations/
 *    force_audiences are present. Does NOT override disable tracking API.
 *
 * @enum {string}
 */
var DirectiveQueryParams = {
  AB_PREVIEW: 'optimizely_show_preview',
  DISABLE: 'optimizely_disable',
  EDITOR: 'optimizely_editor',
  EDITOR_V2: 'optimizely_p13n',  //TODO(will): rename query parameter (CJS-503)
  FORCE_AUDIENCES: 'optimizely_x_audiences',
  FORCE_VARIATIONS: 'optimizely_x',
  LEGACY_PREVIEW: 'optimizely_show_preview',
  OPT_OUT: 'optimizely_opt_out',
  PREVIEW_LAYER_IDS: 'optimizely_preview_layer_ids',
  TOKEN: 'optimizely_token',
  FORCE_TRACKING: 'optimizely_force_tracking',
  EMBED_EDITOR: 'optimizely_embed_editor',
};

/**
 * Populates the directive store with global directive information
 *
 */
exports.populateDirectiveData = function() {
  setLogConfig();
  if (__FILTER_BOTS__) {
    checkForBot();
  }
  checkIfSomeClientAlreadyInitialized();
  checkIfMutationObserversAreSupported();
  checkForOptOut();
  checkDisableQueryParams();
  checkIsEditor();
  checkIsPreview();
  checkIsLegacyPreview();
  checkIsSlave();
  checkDesktopApp();
  checkV2EditorQueryParam();
  checkEmbedEditor();
  if (__PREVIEW__) {
    registerPreviewLayerIds();
  }
  registerProjectToken();
  registerForceAudienceIds();
  registerForceVariationIds();
  checkForceTrackingQueryParam();
};

var YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

/**
 * Set opt out status. Set or remove the opt out cookie, and show a confirmation alert.
 * @param {boolean} shouldOptOut
 */
exports.setOptOut = function(shouldOptOut:boolean) {
  if (shouldOptOut) {
    Logger.warn('Directive / Opting out');
    Cookie.set(enums.COOKIES.OPT_OUT, 'true', { maxAge: YEAR_IN_SECONDS * 10 }, true);
  } else {
    Cookie.remove(enums.COOKIES.OPT_OUT);
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    shouldOptOut: shouldOptOut,
  });
};

function checkIfSomeClientAlreadyInitialized() {
  var alreadyInitialized = Boolean(_.result(__GLOBAL__, 'initialized'));

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    alreadyInitialized: alreadyInitialized,
  });
}

/**
 * Set the boolean which indicates if MutationObservers are supported.
 */
function checkIfMutationObserversAreSupported() {
  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    mutationObserverAPISupported: WindowEnv.isMutationObserverAPISupported(),
  });
}

/**
 * TODO(ajith): CJS-910: Remove this once server-side bot filtering is done (NB-1059).
 * Check if the user-agent is a bot and disable tracking if so.
 * NOTE: Our bot list matches that of Optimizely Classic found here:
 * https://github.com/optimizely/optimizely/blob/5bbc192f7d0ac8c947d0ee1e2e761ba4d78788e8/src/www/js/client/user.js#L169
 * Additional sources are mentioned in comments.
 *
 * @private
 */
function checkForBot() {
  var userAgent = WindowEnv.getUserAgent() || '';
  if (!_.isString(userAgent)) {
    Logger.warn('Directive / userAgent not a string');
    return;
  }

  userAgent = userAgent.toLowerCase();
  var BOT_LIST = ['googlebot', 'yahoo! slurp', 'bingbot', 'bingpreview', 'msnbot', 'keynote', 'ktxn', 'khte',
    'gomezagent', 'alertsite', 'yottaamonitor', 'pingdom.com_bot', 'aihitbot', 'baiduspider', 'adsbot-google',
    'mediapartners-google', 'applebot',
    // NB: The following substrings are a subset of those published by the
    // Interactive Advertising Bureau (IAB) [https://www.iab.com/]
    // Most of the strings above are also part of this published list.
    // See: go/iab
    'catchpoint', 'phantomjs', 'moatbot', 'facebookexternalhit',
  ];
  var matchesBot = function(botSubString) {
    if (_.includes(userAgent, botSubString)) {
      Logger.warn('Directive / Matches bot:', botSubString);
      return true;
    }
  };
  if (_.some(BOT_LIST, matchesBot)) {
    Logger.log('Directive / Disabling tracking');
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      trackingDisabled: true,
    });
  }
}

/**
 * Update visitor's opt out preference from cookie/query
 *
 * The query parameter value is given precedence over the cookie value.
 *
 * Here we only update the store. We intentionally don't set the cookie here but instead do so in
 * Controller.initialize after we've processed cookie-modification APIs such as setCookieDomain in order
 * to respect these APIs.
 *
 * @private
 */
function checkForOptOut() {
  var cookieOptOut = Cookie.get(enums.COOKIES.OPT_OUT);
  var queryOptOut = BrowserSource.getQueryParamValue(DirectiveQueryParams.OPT_OUT);

  var optOutMsg = 'You have successfully opted out of Optimizely for this domain.';
  var optInMsg = 'You are NOT opted out of Optimizely for this domain.';

  var hasQueryOptOut = (queryOptOut === 'true' || queryOptOut === 'false');

  if (hasQueryOptOut) {
    // query overrides cookie
    var shouldOptOut = queryOptOut === 'true';
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      shouldOptOut: shouldOptOut,
    });
    WindowEnv.alert(shouldOptOut ? optOutMsg : optInMsg);
  } else if (cookieOptOut) {
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      shouldOptOut: cookieOptOut === 'true',
    });
  }
}

/**
 * Determine whether the client should activate.
 */
function checkDisableQueryParams() {
  var disableQueryParamsPresent = false;

  // If ab preview mode is enabled, we want to disable the p13n client as they cannot control the
  // p13n actions through the UI and are likely using that preview mode to explicitly preview a/b
  // related things
  var disableParams = [
    DirectiveQueryParams.AB_PREVIEW,
    DirectiveQueryParams.DISABLE,
  ];

  // If `optimizely_editor` parameter is set, it should short-circuit the regular build of client
  // as we don't want it running in the editor. However, the EDITOR build of client should run,
  // as it is specifically intended to be run in the editor environment.
  if (!__EDITOR__) {
    disableParams.push(DirectiveQueryParams.EDITOR);
  }

  for (var i = 0; i < disableParams.length; i++) {
    if (BrowserSource.getQueryParamValue(disableParams[i]) === 'true') {
      Logger.warn('Directive / Not activating because ' + disableParams[i] + ' is set.');
      disableQueryParamsPresent = true;
      break;
    }
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    disabled: disableQueryParamsPresent,
  });
}

/**
 * Determines whether this is a preview build of client
 */
function checkIsPreview() {
  if (__PREVIEW__) {
    Logger.log('Directive / Is Preview mode');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isPreview: __PREVIEW__,
  });
}

/**
 * Determines whether the legacy preview param is present.
 * @private
 */
function checkIsLegacyPreview() {
  var legacyPreviewParam = BrowserSource.getQueryParamValue(DirectiveQueryParams.LEGACY_PREVIEW);
  if (legacyPreviewParam) {
    Logger.log('Directive / Is legacy preview mode');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isLegacyPreview: !!legacyPreviewParam,
  });
}

/**
 * Determines whether this is an editor build of client
 */
function checkIsEditor() {
  if (__EDITOR__) {
    Logger.log('Directive / Is Editor mode');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isEditor: __EDITOR__,
  });
}

/**
 * Determines whether this is a slave build of client
 */
function checkIsSlave() {
  if (__SLAVE__) {
    Logger.log('Directive / Is Slave mode');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isSlave: __SLAVE__,
  });
}

/**
 * Indicates if were running inside the desktop app editor.
 */
function checkDesktopApp() {
  // If this page is being loaded inside the desktop app, a preload script will have defined window.optlyDesktop.
  // optlyDesktop.p13nInner can indicate 3 possible states wrt client:
  //    optlyDesktop.p13nInner === true       |   Running in the v2 editor.
  //    optlyDesktop.p13nInner === false      |   Running in the legacy editor.
  //    optlyDesktop.p13nInner === undefined  |   Not running in the editor.
  //
  // This behavior is defined in the optimizely-desktop repo:
  //   https://github.com/optimizely/optimizely-desktop/commit/4b443eb33cdfc15e152cd5deb82c45901aa37157
  var optlyDesktop = WindowEnv.getGlobal('optlyDesktop');
  // If optlyDesktop.p13nInner is defined, were running in the desktop app in one of the editors. The value is not relevant.
  var inDesktopApp = !!(optlyDesktop && !_.isUndefined(optlyDesktop['p13nInner']));

  if (inDesktopApp) {
    Logger.log('Directive / Is running in desktop app editor');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isRunningInDesktopApp: inDesktopApp,
  });
}

/**
 * Determines whether this is running in v2 editor
 * @private
 */
function checkV2EditorQueryParam() {
  var inV2Editor = BrowserSource.getQueryParamValue(DirectiveQueryParams.EDITOR_V2) === 'true';

  if (inV2Editor) {
    Logger.log('Directive / Is running in editor');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    isRunningInV2Editor: inV2Editor,
  });
}

/**
 * Determines whether this is running in embedded editor
 * @private
 */
function checkEmbedEditor() {
  // Check query param or sessionStorage for embedded editor
  let shouldEmbed = false;
  const SessionStorage = WindowEnv.getGlobal('sessionStorage');
  var requestedEmbeddedEditor = BrowserSource.getQueryParamValue(DirectiveQueryParams.EMBED_EDITOR);
  shouldEmbed = requestedEmbeddedEditor === 'true';
  if (requestedEmbeddedEditor && SessionStorage) {
    if (requestedEmbeddedEditor === 'true') {
      SessionStorage.setItem(DirectiveQueryParams.EMBED_EDITOR, 'true');
    } else SessionStorage.removeItem(DirectiveQueryParams.EMBED_EDITOR);  // Allow unsetting via query param
  }

  if (SessionStorage && SessionStorage.getItem(DirectiveQueryParams.EMBED_EDITOR) === 'true') {
    shouldEmbed = true;
    Logger.log('Directive / Should mount embedded editor');
  }

  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    requestedEmbeddedEditor: shouldEmbed,
  });
}

/**
 * Retrieve the layer ids that are specified for previewing to pass along to the preview_layer_data endpoint.
 * First check the query parameters passed to client.
 * Then check the preview data cookie.
 *
 */
function registerPreviewLayerIds() {
  var previewCookie = Cookie.get(enums.COOKIES.PREVIEW);
  var previewLayerIdsParam = BrowserSource.getQueryParamValue(DirectiveQueryParams.PREVIEW_LAYER_IDS);
  // Temporarily check for the old query param value until the app is updated to serve the new one.
  var legacyPreviewLayerIdsParam = BrowserSource.getQueryParamValue('preview_layer_ids');
  var previewLayerIds = null;

  // if optimizely_preview_layer_ids is in the querystring, return an array of
  // the layers ids which we are previewing working copy data for.
  if (previewLayerIdsParam) {
    previewLayerIds = Cookie.safeDecodeURIComponent(previewLayerIdsParam).split(',');

  // Todo(James): Remove this as soon as app is updated.
  } else if (legacyPreviewLayerIdsParam) {
    previewLayerIds = legacyPreviewLayerIdsParam.split(',');
  } else if (previewCookie) {
    try {
      var previewData = JSONEnv.parse(previewCookie);
      previewLayerIds = previewData.previewLayerIds;
    } catch (error) {
      var newError = new JSONParseError('Failed to parse previewCookie in registerPreviewLayerIds: ' + previewCookie);
      var metadata = {
        originalMessage: error.message,
        userError: true,
      };
      ApiEvents.emitError(newError, metadata);
    }
  }

  if (previewLayerIds) {
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      previewLayerIds: previewLayerIds,
    });

    Logger.log('Directive / Previewing Campaign(s):', previewLayerIds);
  }
}

/**
 * Retrieve the hashed token unique to this account supplied in the query string or cookie.
 *
 */
function registerProjectToken() {
  var cookieToken = Cookie.get(enums.COOKIES.TOKEN) || null;
  var projectToken = BrowserSource.getQueryParamValue(DirectiveQueryParams.TOKEN) || cookieToken;
  Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
    projectToken: projectToken,
  });
}

/**
 * Retrieve force audience ids specified in either the querystring or the preview cookie.
 *
 * Querystring expected format is:
 *   "optimizely_x_audiences=1,24,5"
 *
 * Preview cookie expected format is:
 *   {
 *     ...
 *     forcedAudienceIds: [],
 *     forcedVariationIds: [],
 *     ...
 *   }
 *
 */
function registerForceAudienceIds() {
  var previewCookie = Cookie.get(enums.COOKIES.PREVIEW);
  var forceAudienceIds = [];
  var forceAudiencesParam = BrowserSource.getQueryParamValue(DirectiveQueryParams.FORCE_AUDIENCES);

  // if optimizely_x_audiences is in the querystring, return an array of
  // audience overrides
  if (forceAudiencesParam) {
    forceAudienceIds = Cookie.safeDecodeURIComponent(forceAudiencesParam).split(',');
  } else if (previewCookie) {
    try {
      var previewData = JSONEnv.parse(previewCookie);
      forceAudienceIds = previewData.forceAudienceIds;
    } catch (error) {
      var newError = new JSONParseError('Failed to parse previewCookie in registerForceAudienceIds: ' + previewCookie);
      var metadata = {
        originalMessage: error.message,
        userError: true,
      };
      ApiEvents.emitError(newError, metadata);
    }
  }

  if (forceAudienceIds.length) {
    if (__ENABLE_FORCE_PARAMS__) {
      Logger.log('Directive / Force Audience IDs:', forceAudienceIds);
      Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
        forceAudienceIds: forceAudienceIds,
      });
    } else {
      Logger.warn('Directive / Force parameters disabled - ignoring force audience IDs.');
    }
  }
}

/**
 * Retrieve force variation ids specified in either the querystring or the preview cookie.
 *
 * Querystring expected format is:
 *   "optimizely_x=1,24,5"
 *
 * Preview cookie expected format is:
 *   {
 *     ...
 *     forcedAudienceIds: [],
 *     forcedVariationIds: [],
 *     ...
 *   }
 *
 */
function registerForceVariationIds() {
  var previewCookie = Cookie.get(enums.COOKIES.PREVIEW);
  var forceVariationIds = [];
  var forceVariationsParam = BrowserSource.getQueryParamValue(DirectiveQueryParams.FORCE_VARIATIONS);

  // if optimizely_x is in the querystring, return an array of
  // variation overrides
  if (forceVariationsParam) {
    forceVariationIds = Cookie.safeDecodeURIComponent(forceVariationsParam).split(',');
  } else if (previewCookie) {
    try {
      var previewData = JSONEnv.parse(previewCookie);
      forceVariationIds = previewData.forceVariationIds;
    } catch (error) {
      var newError = new JSONParseError('Failed to parse previewCookie in registerForceVariationIds: ' + previewCookie);
      var metadata = {
        originalMessage: error.message,
        userError: true,
      };
      ApiEvents.emitError(newError, metadata);
    }
  }

  if (forceVariationIds.length) {
    if (__ENABLE_FORCE_PARAMS__) {
      Logger.log('Directive / Force Variation IDs:', forceVariationIds);
      Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
        forceVariationIds: forceVariationIds,
      });
    } else {
      Logger.warn('Directive / Force parameters disabled - ignoring force variation IDs.');
    }
  }
}

/**
 * Retrieve force tracking query param
 *
 * Querystring expected format is:
 *   "optimizely_force_tracking=true"
 */
function checkForceTrackingQueryParam() {
  var forceTracking = BrowserSource.getQueryParamValue(DirectiveQueryParams.FORCE_TRACKING);
  if (forceTracking) {
    Flux.dispatch(actionTypes.LOAD_DIRECTIVE, {
      forceTracking: forceTracking,
    });
  }
}

/**
 * Set log config from query params or built-in default
 * Reads the query params as optimizely_log=<level>:<match>
 * @private
 */
function setLogConfig() {
  var logLevel = __LOGLEVEL__;

  var queryLogLevel = BrowserSource.getQueryParamValue('optimizely_log');
  if (queryLogLevel) {
    var logParams = queryLogLevel.split(':');
    if (logParams[0] !== '') {
      logLevel = String(logParams[0]).toUpperCase();
    }

    if (typeof logParams[1] !== 'undefined') {
      Logger.setLogMatch(logParams[1]);
    }
  }

  Logger.setLogLevel(logLevel);
}

if (__TEST__) {
  exports.checkIfSomeClientAlreadyInitialized = checkIfSomeClientAlreadyInitialized;
  exports.checkV2EditorQueryParam = checkV2EditorQueryParam;
  exports.checkEmbedEditor = checkEmbedEditor;
  exports.checkDesktopApp = checkDesktopApp;
  exports.checkDisableQueryParams = checkDisableQueryParams;
  exports.checkForBot = checkForBot;
  exports.checkForOptOut = checkForOptOut;
  exports.registerForceAudienceIds = registerForceAudienceIds;
  exports.registerForceVariationIds = registerForceVariationIds;
  exports.registerPreviewLayerIds = registerPreviewLayerIds;
  exports.registerProjectToken = registerProjectToken;
  exports.DirectiveQueryParams = DirectiveQueryParams;
  exports.checkForceTrackingQueryParam = checkForceTrackingQueryParam;
}
